<template>
  <button class="product-option-value" @click.prevent="onClick" :class="classes">
    <span class="product-option-value__text">{{ value }}</span>
  </button>
</template>

<script lang="ts" setup>
import { computed, unref } from 'vue'
import {
  ProductFieldsFragment,
  ProductOptionFieldsFragment,
  VariantFieldsFragment,
} from '_library/src/storefront/generated'
import { findSelectedVariant, mapGraph, mergeProductOptions } from '_library'
import { ProductSelectionType } from '../../composables/useProductSelection'

const props = defineProps<{
  product: ProductFieldsFragment
  selection: ProductSelectionType
  option: ProductOptionFieldsFragment
  value: string
}>()

const variants = mapGraph(props.product.variants) as VariantFieldsFragment[]

const pairEnabled = computed(() => props.product.availableAsPair?.value === 'true')

const isPairValue = computed(
  () => pairEnabled.value && props.option.name === 'QTY' && props.value === 'Pair'
)

const selectedOptions = computed(() => unref(props.selection.selectedOptions))

const selectedOption = computed(() =>
  selectedOptions.value?.find((option) => option.name == props.option.name)
)

/*
 * Pair is a storefront-only option. When resolving the actual
 * Shopify variant, treat Pair as Single.
 */
const variantLookupOptions = computed(() => {
  const mergedOptions = mergeProductOptions(
    selectedOptions.value,
    `${props.option?.name}`,
    `${props.value}`
  )

  if (!pairEnabled.value) return mergedOptions

  return mergedOptions.map((option) => {
    if (option.name === 'QTY' && option.value === 'Pair') {
      return {
        ...option,
        value: 'Single',
      }
    }

    return option
  })
})

const variant = computed(() => findSelectedVariant(variants, variantLookupOptions.value))

const isInStock = computed(() => {
  if (!variant.value?.availableForSale) return false

  const quantity = variant.value?.quantityAvailable || 0

  if (isPairValue.value) {
    return quantity >= 2
  }

  return quantity > 0
})

const classes = computed(() => [
  `product-option-value--${selectedOption.value?.value == props.value ? 'active' : 'inactive'}`,
  `product-option-value--${variant.value?.availableForSale ? 'available' : 'unavailable'}`,
  `product-option-value--${isInStock.value ? 'in-stock' : 'made-to-order'}`,
])

const onClick = () => {
  if (!variant.value) return

  props.selection.selectOptionVariant(variant.value, props.option.name, props.value)
}
</script>

<style lang="scss">
@import '~styles/base.scss';

.product-option-value {
  &--inactive {
    .product-option-value {
      &__text {
        color: var(--color-midgrey);
      }
    }
  }

  &--unavailable {
    pointer-events: none;
    .product-option-value {
      &__text {
        text-decoration: line-through;
      }
    }
  }

  &--in-stock {
    .product-option-value {
      &__text {
        &:after {
          content: '*';
        }
      }
    }
  }

  @include breakpoint('l') {
    border: 1px solid #b3b3b3;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 64px;
    height: auto;
    padding: 8px 8px 8px;
    transition: border-color 0.2s ease;
  }

  &:hover:not(.product-option-value--unavailable),
  &--active {
    @include breakpoint('l') {
      border-color: var(--color-dark);
    }

    .product-option-value {
      &__text {
        @include breakpoint('l+') {
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
